import {
  Button,
  Modal,
  Tooltip,
  TreeSelect,
  DatePicker,
  notification,
} from 'antd'
import React, { useState } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import { useDispatch } from 'react-redux'
import { fetchGrossProfitAndSalesForVenues } from 'src/features/WeeklySalesSlice'
import { saveAs } from 'file-saver'
import { VENUES_REPORT_DESCRIPTIONS } from './venuesReportDescriptions'
import ReportGroupSelect from './ReportGroupSelect'
import {
  flashReportLargeGroupQuestion,
  shouldConfirmFlashReportSize,
} from './flashReportConfirm'

const CombinedFlashReport = ({ venues }) => {
  const [selectedVenueIds, setSelectedVenuesIds] = useState([])
  const dispatch = useDispatch()
  const dateFormat = 'YYYY/MM/DD'
  const venueOptions = venues?.map(item => ({
    title: item.title,
    value: item._id,
  }))
  const allIds = venueOptions?.map(({ value }) => value)
  const [date, setDate] = useState({
    start: moment().subtract(40, 'days').format(dateFormat),
    end: moment().format(dateFormat),
  })
  const [isLoading, setIsLoading] = useState(false)
  const onChangeDate = date => {
    setDate({ start: date[0], end: date[1] })
  }

  const handleDownloadCSV = async () => {
    setIsLoading(true)

    try {
      const resultAction = await dispatch(
        fetchGrossProfitAndSalesForVenues(
          date.start,
          date.end,
          selectedVenueIds,
        ),
      )

      const csvRows = [
        [
          'Date',
          'Venue Name',
          'Sales',
          'Misc Costs (manual entry)',
          'OMS Spend (auto, ex VAT)',
          'GP % (Excl. Misc Costs)',
          'Gross Profit %',
          'Excluded products',
          'Excluded spend (ex VAT)',
          'Forecasted Sales',
          'Variance',
        ],
      ]

      const data = resultAction || []

      data.forEach(venue => {
        const venueName = venue?.venueTitle?.title || 'N/A'

        venue.salesPerDay.forEach(day => {
          // const purchases = (day.purchases || [])
          //   .map(p => `${p.text}: ${p.amount}`)
          //   .join('; ')

          const variance =
            day.forecastedSales != null
              ? (day.sales - day.forecastedSales).toFixed(2)
              : ''

          csvRows.push([
            day.date || '',
            venueName,
            day.sales ?? 0,
            day.miscCostPerDay ?? 0,
            day.purchasesOnOMS ?? 0,
            venue.gppExclMisc ?? 0,
            venue.gpp ?? '0',
            venue.excludedThisPeriod?.productCount ?? 0,
            venue.excludedThisPeriod?.spend ?? 0,
            day.forecastedSales ?? 0,
            variance,
          ])
        })
      })

      const csvContent = csvRows.map(e => e.join(',')).join('\n')
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      saveAs(blob, `Flash_Report_${moment().format('YYYY-MM-DD')}.csv`)
    } catch (err) {
      console.error('CSV download failed:', err)
      const description =
        err?.response?.data?.message ||
        'We could not download the Combined Flash Report. Please try again.'

      notification.error({
        message: 'Report failed',
        description,
      })
    } finally {
      setIsLoading(false)
    }
  }

  const askThenDownload = () => {
    if (!shouldConfirmFlashReportSize(selectedVenueIds.length)) {
      handleDownloadCSV()
      return
    }
    Modal.confirm({
      title: 'Large flash report',
      content: flashReportLargeGroupQuestion(selectedVenueIds.length),
      okText: 'Generate report',
      cancelText: 'Cancel',
      onOk: () => handleDownloadCSV(),
    })
  }

  return (
    <StyledBlock>
      <HelperText data-testid='report-helper'>
        {VENUES_REPORT_DESCRIPTIONS['Combined Flash Report']}
      </HelperText>
      <ReportGroupSelect venues={venues} onPick={setSelectedVenuesIds} />
      <TreeSelect
        treeNodeFilterProp='title'
        allowClear={true}
        placeholder='Select Venues ...'
        treeCheckable={true}
        showCheckedStrategy={TreeSelect.SHOW_CHILD}
        dropdownStyle={{ maxHeight: '300px' }}
        style={{ marginBottom: '15px', width: '355px' }}
        onChange={ids => setSelectedVenuesIds(ids)}
        value={selectedVenueIds}
        treeData={[
          {
            title:
              selectedVenueIds.length > 0 ? (
                <span
                  onClick={() => setSelectedVenuesIds([])}
                  style={{
                    display: 'inline-block',
                    color: '#b5d922',
                    cursor: 'pointer',
                  }}
                >
                  Unselect all
                </span>
              ) : (
                <span
                  onClick={() => setSelectedVenuesIds(allIds)}
                  style={{
                    display: 'inline-block',
                    color: '#b5d922',
                    cursor: 'pointer',
                  }}
                >
                  Select all
                </span>
              ),
            value: 'xxx',
            disableCheckbox: true,
            disabled: true,
          },
          ...venueOptions,
        ]}
      />
      <StyledRangePicker
        defaultValue={[moment().subtract(40, 'days'), moment()]}
        onChange={(value, string) => onChangeDate(string)}
        format={dateFormat}
      />
      {selectedVenueIds.length === 0 ? (
        <Tooltip title={'Seleect at least one venue'}>
          <Button
            style={{ width: '355px', marginTop: 20 }}
            className='ant-btn ant-btn-primary'
          >
            Download CSV Report
          </Button>
        </Tooltip>
      ) : (
        <Button
          loading={isLoading}
          style={{ width: '355px', marginTop: 20 }}
          className='ant-btn ant-btn-primary'
          onClick={askThenDownload}
          disabled={isLoading}
        >
          Download CSV Report
        </Button>
      )}
    </StyledBlock>
  )
}

const StyledBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const HelperText = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 10px;
`
const StyledRangePicker = styled(DatePicker.RangePicker)`
  .ant-calendar-picker-input.ant-input {
    height: 40px;
    padding: 6px 6px;
    font-size: 16px;
    width: 355px;
  }
`

export default CombinedFlashReport
